import type { PluginListingEntry } from '@crowdex/agent-contracts/api';
import type { AppearanceVariant } from '@crowdex/ui/lib/appearance/chrome-theme';
import { type CodeThemePair, CodeThemesProvider } from '@crowdex/ui/lib/appearance/code-themes';
import {
  type TerminalPalette,
  terminalPaletteFromVscodeTheme,
  type VscodeTheme,
} from '@crowdex/ui/lib/appearance/vscode-theme';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { listApps } from '@/features/apps/apps-client';
import { useActiveAppearance } from './active-appearance';
import { CROWDEX_CODE_THEME, isDefaultChrome } from './appearance';
import {
  type CodeThemeOption,
  codeThemeCacheKey,
  pluginCodeThemes,
  resolveCodeTheme,
  shikiCodeTheme,
  useImportedThemesRevision,
} from './code-themes';
import { TERMINAL_THEME } from './terminal-palette';

const PLUGINS_CHANGED_EVENT = 'crowdex-plugins-changed';

type PluginCodeTheme = CodeThemeOption & { url: string };

/**
 * The code themes enabled plugins contribute. Only listed while `enabled`: most appearances use
 * no plugin theme, and signed-out pages have no plugins to ask about.
 */
export function usePluginCodeThemes(enabled: boolean): PluginCodeTheme[] {
  const [themes, setThemes] = useState<PluginCodeTheme[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = () =>
      listApps().then(
        (plugins: PluginListingEntry[]) => {
          if (!cancelled) setThemes(pluginCodeThemes(plugins));
        },
        () => undefined,
      );
    void load();
    window.addEventListener(PLUGINS_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(PLUGINS_CHANGED_EVENT, load);
    };
  }, [enabled]);
  return themes;
}

/**
 * The VS Code data of a code theme, or `null` while it loads, when it cannot, and when no enabled
 * plugin offers it any more.
 */
function useResolvedCodeTheme(
  id: string,
  variant: AppearanceVariant,
  pluginThemes: readonly PluginCodeTheme[],
): VscodeTheme | null {
  const [theme, setTheme] = useState<{ key: string; value: VscodeTheme } | null>(null);
  const importedRevision = useImportedThemesRevision();
  const pluginReady = !id.startsWith('plugin:') || pluginThemes.some((theme) => theme.id === id);
  // A plugin's theme is the one of its current build; an imported one is reloaded when replaced.
  const key = `${codeThemeCacheKey(id, variant, pluginThemes)}:${variant}:${
    id.startsWith('imported:') ? importedRevision : 0
  }`;
  useEffect(() => {
    if (!pluginReady) return;
    let cancelled = false;
    resolveCodeTheme(id, variant, pluginThemes).then(
      (value) => {
        if (!cancelled) setTheme({ key, value });
      },
      () => {
        if (!cancelled) setTheme(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [id, key, variant, pluginReady, pluginThemes]);
  return pluginReady && theme?.key === key ? theme.value : null;
}

const TerminalPaletteContext = createContext<TerminalPalette | null>(null);

/** The terminal palette of the appearance shown, or `null` for the app's own. */
export function useTerminalPalette(): TerminalPalette | null {
  return useContext(TerminalPaletteContext);
}

/**
 * Gives code blocks the code themes of the light and dark appearance, and terminals the palette
 * of the one shown. The chrome itself is a stylesheet `applyAppSettings` maintains.
 */
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const { appearance, variant } = useActiveAppearance();
  const lightId = appearance.light.codeTheme;
  const darkId = appearance.dark.codeTheme;
  const pluginThemes = usePluginCodeThemes(
    lightId.startsWith('plugin:') || darkId.startsWith('plugin:'),
  );
  const light = useResolvedCodeTheme(lightId, 'light', pluginThemes);
  const dark = useResolvedCodeTheme(darkId, 'dark', pluginThemes);
  const themes = useMemo<CodeThemePair>(
    () => [shikiCodeTheme(lightId, 'light', light), shikiCodeTheme(darkId, 'dark', dark)],
    [lightId, darkId, light, dark],
  );
  const shown = variant === 'dark' ? dark : light;
  const shownId = appearance[variant].codeTheme;
  const chrome = appearance[variant].chrome;
  const defaultLook = shownId === CROWDEX_CODE_THEME && isDefaultChrome(appearance, variant);
  const palette = useMemo(
    () =>
      defaultLook || !shown ? null : terminalPaletteFromVscodeTheme(shown, chrome, TERMINAL_THEME),
    [defaultLook, shown, chrome],
  );
  // Terminal surfaces outside xterm (tool output, the panel's padding) take the same colors.
  useEffect(() => {
    const root = document.documentElement.style;
    if (!palette) {
      root.removeProperty('--desktop-terminal');
      root.removeProperty('--desktop-terminal-foreground');
      return;
    }
    root.setProperty('--desktop-terminal', palette['background'] ?? '');
    root.setProperty('--desktop-terminal-foreground', palette['foreground'] ?? '');
  }, [palette]);
  return (
    <CodeThemesProvider themes={themes}>
      <TerminalPaletteContext.Provider value={palette}>{children}</TerminalPaletteContext.Provider>
    </CodeThemesProvider>
  );
}
